import { useEffect, useState } from 'react';
import {
    Controller,
    SubmitErrorHandler,
    SubmitHandler,
    useForm
} from 'react-hook-form';
import { InputField } from '@zoopla-eng/zds-components/input-field';
import { Button } from '@zoopla-eng/zds-components/button';
import { Box } from '@zoopla-eng/zds-components/box';
import { fontGroups } from '@zoopla-eng/zds-components/themes';
import { Link } from '@zoopla-eng/zds-components/link';

import { triggerTracking } from '@zoopla/tracking';
import { isFieldError, useRegister } from '../../hooks/useRegister';
import { EMAIL_REGEX, PASSWORD_MIN_LENGTH } from '../../utils/validation/rules';
import { hasNthErrors } from '../../utils/hasNthErrors';
import { SocialAuth } from '../SocialAuth';
import { InputPassword } from '../InputPassword';
import { EmailPreferencesToggle } from '../EmailPreferencesToggle';
import {
    buttonContainerStyle,
    reassuranceTextStyle,
    styledGeneralErrorStyle
} from '../shared/shared.styles.css';
import { AuthMutationHookConsumerProps } from '../../hooks/authMutationHook.types';
import { ErrorAlert } from '../ErrorAlert';
import { termsTextStyle } from './RegisterForm.styles.css';

const detailsPasswordFieldGuidance =
    'Password must be at least 8 characters containing 1 uppercase letter, 1 lowercase letter, 1 number and 1 special character';
const minimumLengthGuidance = 'Minimum 8 characters';

const socialCopy = {
    cta: 'or continue with your email',
    facebook: 'Facebook',
    google: 'Google'
};

type RegistrationFormData = {
    email: string;
    password: string;
    consent__insights__24: 'no' | 'yes';
};

type RegisterFormProps = {
    prefilledEmail?: string;
} & AuthMutationHookConsumerProps;

export const RegisterForm = ({
    prefilledEmail,
    onAuthentication,
    trackingData
}: RegisterFormProps) => {
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const {
        register,
        handleSubmit,
        setError: setFieldError,
        formState: { errors },
        reset,
        getFieldState,
        getValues,
        control
    } = useForm<RegistrationFormData>({
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        defaultValues: {
            email: prefilledEmail || undefined
        }
    });

    const registerUser = useRegister({
        trackingData
    });

    const resetFormErrors = () => {
        // reset register form general and field-specific errors
        setError('');

        const { consent__insights__24, ...currentValues } = getValues();
        const consentState = getFieldState('consent__insights__24');

        reset({
            ...currentValues,
            ...(consentState.isTouched &&
                !consentState.error && {
                    consent__insights__24
                })
        });
    };

    const onFormSubmit: SubmitHandler<RegistrationFormData> = async (
        formData
    ) => {
        setIsLoading(true);
        resetFormErrors();

        try {
            const user = await registerUser({
                ...formData,
                consent__insights__24:
                    formData.consent__insights__24 === 'yes' ? true : false
            });

            await onAuthentication(user);
        } catch (error) {
            if (error instanceof Error) {
                if (isFieldError(error)) {
                    setFieldError(
                        error.field,
                        {
                            type: 'manual',
                            message: error.message
                        },
                        { shouldFocus: true }
                    );
                } else {
                    setError(error.message);
                }
            } else {
                setError('Something went wrong');
            }

            // we only need to update the loading state if we have an error
            // in case everything works as intended the user will be moved to the next step
            setIsLoading(false);
        }
    };

    const onFormSubmitError: SubmitErrorHandler<RegistrationFormData> = (
        errors
    ) => {
        if (
            errors.consent__insights__24 &&
            hasNthErrors({ expectedErrors: 1, formErrors: errors })
        ) {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: 'registration',
                eventAction: 'failure_must_select_opt_in',
                eventLabel: trackingData.label
            });
        }
    };

    const onSubmit = handleSubmit(onFormSubmit, onFormSubmitError);

    useEffect(() => {
        reset();
    }, [reset]);

    return (
        <>
            <SocialAuth
                onAuthentication={onAuthentication}
                trackingData={trackingData}
                copy={socialCopy}
                type="register"
                shouldReset={isLoading}
                resetErrors={resetFormErrors}
            />
            <form
                onSubmit={onSubmit}
                noValidate
                data-testid="auth-modal-registration-form"
                method="post"
            >
                <ErrorAlert>
                    {error && (
                        <Box pb="S500">
                            <span
                                className={`${styledGeneralErrorStyle} ${fontGroups.fork}`}
                                data-testid="general-error"
                            >
                                Sorry, something went wrong. Please try again.
                            </span>
                        </Box>
                    )}
                </ErrorAlert>

                <Box pb="S500">
                    <InputField
                        label="Email address"
                        aria-required
                        type="email"
                        autoComplete="email"
                        errorText={
                            errors.email &&
                            (errors.email.message ||
                                'Please enter a valid email address')
                        }
                        {...register('email', {
                            required: true,
                            pattern: EMAIL_REGEX
                        })}
                    />
                </Box>
                <Box pb="M100" bpM100={{ pb: 'M300' }}>
                    <InputPassword
                        autoComplete="new-password"
                        label="Create password"
                        aria-required
                        helpText={detailsPasswordFieldGuidance}
                        errorText={
                            errors.password &&
                            (errors.password.message ||
                                detailsPasswordFieldGuidance)
                        }
                        {...register('password', {
                            required: true,
                            minLength: {
                                value: PASSWORD_MIN_LENGTH,
                                message: minimumLengthGuidance
                            },
                            validate: {
                                // Change those to specific message when we have the copy in V2
                                hasSpecialChar: (v: string) =>
                                    /[\W_]/.test(v) ||
                                    detailsPasswordFieldGuidance,
                                hasNumber: (v: string) =>
                                    /[0-9]/.test(v) ||
                                    detailsPasswordFieldGuidance,
                                hasUppercase: (v: string) =>
                                    /[A-Z]/.test(v) ||
                                    detailsPasswordFieldGuidance,
                                hasLowercase: (v: string) =>
                                    /[a-z]/.test(v) ||
                                    detailsPasswordFieldGuidance
                            }
                        })}
                    />
                </Box>

                <Box pb="M100" bpM100={{ pb: 'S500' }}>
                    <Controller
                        control={control}
                        name="consent__insights__24"
                        rules={{ required: true }}
                        render={({
                            field: { onChange, name, ref, onBlur }
                        }) => (
                            <EmailPreferencesToggle
                                legend="Email preferences"
                                label="I want to hear about the latest property news, advice and new home developments from Zoopla and its selected partners only."
                                errorText={
                                    errors.consent__insights__24 &&
                                    'Please set your email preferences.'
                                }
                                name={name}
                                onChange={onChange}
                                onBlur={onBlur}
                                ref={ref}
                            />
                        )}
                    />
                </Box>

                <Box pb="M100">
                    <p
                        className={`${reassuranceTextStyle} ${fontGroups.fork}`}
                        data-testid="unsubscribe-text"
                    >
                        You can unsubscribe or update your preferences at any
                        time.
                    </p>
                </Box>

                <Box pb="S500" display="flex" justifyContent="center">
                    <div className={buttonContainerStyle}>
                        <Button
                            category="primary"
                            loading={isLoading}
                            loadingText="Registering"
                            data-testid="register-button"
                            fw
                        >
                            Register
                        </Button>
                    </div>
                </Box>
            </form>

            <p className={`${termsTextStyle} ${fontGroups.fork}`}>
                By registering you accept our <br />{' '}
                <Link
                    key="terms"
                    data-testid="terms-link"
                    href="/terms/"
                    onClick={() => {
                        triggerTracking({
                            event: 'GAEvent',
                            eventCategory: 'registration',
                            eventAction: 'click_to_terms',
                            eventLabel: trackingData.label
                        });
                    }}
                >
                    Terms of Use
                </Link>{' '}
                and{' '}
                <Link
                    key="privacy"
                    data-testid="privacy-link"
                    href="/privacy/"
                    onClick={() => {
                        triggerTracking({
                            event: 'GAEvent',
                            eventCategory: 'registration',
                            eventAction: 'click_to_privacy',
                            eventLabel: trackingData.label
                        });
                    }}
                >
                    Privacy Policy
                </Link>
                .
            </p>
        </>
    );
};
